import React, { useState, useEffect } from "react";
import API from "../config/api";
import "../styles/Account.css";
import { FaQrcode, FaCreditCard, FaCheckCircle, FaMagic, FaEye } from "react-icons/fa";

function PaymentSettings() {
  const [keys, setKeys] = useState({
    razorpayKeyId: "",
    razorpayKeySecret: "",
    gstRate: 18,
    enableRazorpay: true,
    enableUpi: true,
    upiId: ""
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const res = await API.get("/api/payment-settings", { params: { _t: Date.now() } });
      if (res.data?.success) {
        setKeys({
          razorpayKeyId: res.data.razorpayKeyId || "",
          razorpayKeySecret: res.data.razorpayKeySecret || "",
          gstRate: res.data.gstRate ?? 18,
          enableRazorpay: res.data.enableRazorpay ?? true,
          enableUpi: res.data.enableUpi ?? true,
          upiId: res.data.upiId || ""
        });
      }
    } catch (error) {
      console.error("Error fetching payment settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setKeys((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = await API.post("/api/payment-settings", keys);
      if (res.data?.success) {
        const settingsData = res.data.settings || res.data;
        setKeys({
          razorpayKeyId: settingsData.razorpayKeyId || "",
          razorpayKeySecret: settingsData.razorpayKeySecret || "",
          gstRate: settingsData.gstRate ?? 18,
          enableRazorpay: settingsData.enableRazorpay ?? true,
          enableUpi: settingsData.enableUpi ?? true,
          upiId: settingsData.upiId || ""
        });
        alert("Payment settings updated successfully! 💳\nDynamic QR Code with your updated UPI ID is now active live on both websites.");
      }
    } catch (error) {
      console.error("Error updating payment settings:", error);
      alert(error.response?.data?.message || "Failed to update payment settings ❌");
    } finally {
      setSaving(false);
    }
  };

  const effectiveUpiId = keys.upiId ? keys.upiId.trim() : "";
  const previewUpiUri = effectiveUpiId ? `upi://pay?pa=${encodeURIComponent(effectiveUpiId)}&pn=${encodeURIComponent("NewsGhuru")}&tn=${encodeURIComponent("NewsGhuru Payment")}` : "";
  const previewQrUrl = previewUpiUri ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(previewUpiUri)}` : "";

  return (
    <div className="account-page">
      <div className="account-header">
        <h1 className="account-title">Payment Gateway & UPI Controls</h1>
        <p className="account-subtitle">
          Manage visible payment methods (Razorpay & Dynamic UPI QR Code), set your business UPI ID, and configure tax rates.
        </p>
      </div>

      <div className="account-sections">
        <div className="account-card" style={{ maxWidth: "720px", margin: "0 auto" }}>
          {loading ? (
            <div style={{ padding: "30px", textAlign: "center", color: "#888" }}>Loading payment configuration...</div>
          ) : (
            <form onSubmit={handleSubmit} className="account-form">
              
              {/* Decoy inputs to absorb browser password manager autofill */}
              <input 
                type="text" 
                name="prevent_autofill_username" 
                autoComplete="off" 
                style={{ position: "absolute", top: "-9999px", left: "-9999px", opacity: 0, width: 0, height: 0, zIndex: -1 }} 
              />
              <input 
                type="password" 
                name="prevent_autofill_password" 
                autoComplete="new-password" 
                style={{ position: "absolute", top: "-9999px", left: "-9999px", opacity: 0, width: 0, height: 0, zIndex: -1 }} 
              />

              {/* SECTION 1: PAYMENT METHOD VISIBILITY TOGGLES */}
              <div style={{ background: "rgba(234, 88, 12, 0.04)", border: "1px solid rgba(234, 88, 12, 0.2)", borderRadius: "12px", padding: "20px", marginBottom: "25px" }}>
                <h3 style={{ fontSize: "1.1rem", fontWeight: "700", marginBottom: "12px", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "8px" }}>
                  <FaCreditCard style={{ color: "#ea580c" }} /> Payment Method Visibility Controls
                </h3>
                <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "16px" }}>
                  Enable or hide payment options displayed to readers across Subscriptions, Advertisements, and Sponsorships checkouts.
                </p>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  {/* Razorpay Toggle */}
                  <label style={{ display: "flex", alignItems: "center", gap: "12px", background: "#fff", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      name="enableRazorpay"
                      checked={keys.enableRazorpay}
                      onChange={handleChange}
                      style={{ width: "20px", height: "20px", accentColor: "#ea580c" }}
                    />
                    <div>
                      <div style={{ fontWeight: "700", fontSize: "0.95rem" }}>Razorpay Gateway</div>
                      <div style={{ fontSize: "0.78rem", color: "#64748b" }}>Credit/Debit Cards, Netbanking</div>
                    </div>
                  </label>

                  {/* UPI QR Code Toggle */}
                  <label style={{ display: "flex", alignItems: "center", gap: "12px", background: "#fff", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      name="enableUpi"
                      checked={keys.enableUpi}
                      onChange={handleChange}
                      style={{ width: "20px", height: "20px", accentColor: "#ea580c" }}
                    />
                    <div>
                      <div style={{ fontWeight: "700", fontSize: "0.95rem" }}>Dynamic UPI QR Code</div>
                      <div style={{ fontSize: "0.78rem", color: "#64748b" }}>GPay, PhonePe, Paytm, Navi, BHIM</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* SECTION 2: DYNAMIC UPI QR CONFIGURATION & LIVE PREVIEW */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", marginBottom: "25px" }}>
                <h3 style={{ fontSize: "1.1rem", fontWeight: "700", marginBottom: "12px", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "8px" }}>
                  <FaQrcode style={{ color: "#16a34a" }} /> Business UPI ID & Live QR Preview
                </h3>
                <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "16px" }}>
                  Enter your business UPI ID (VPA). A Dynamic QR Code with **exact auto-prefilled checkout amounts** will be generated and previewed live below!
                </p>

                <div className="form-group">
                  <label style={{ fontWeight: "600" }}>Business UPI ID (VPA)</label>
                  <input
                    type="text"
                    name="upiId"
                    value={keys.upiId}
                    onChange={handleChange}
                    placeholder="e.g. shaarika.rajan@okicici or newsghuru@upi"
                    required
                    autoComplete="off"
                  />
                  <small style={{ color: "#64748b", marginTop: "4px", display: "block" }}>
                    Payments sent to this UPI ID will go directly to your linked business bank account.
                  </small>
                </div>

                {/* LIVE QR CODE PREVIEW CARD */}
                {effectiveUpiId && (
                  <div style={{
                    marginTop: "20px",
                    background: "#ffffff",
                    border: "2px solid #16a34a",
                    borderRadius: "12px",
                    padding: "20px",
                    textAlign: "center",
                    maxWidth: "340px",
                    margin: "20px auto 0 auto",
                    boxShadow: "0 6px 20px rgba(22, 163, 74, 0.12)"
                  }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#dcfce7", color: "#15803d", padding: "5px 14px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: "800", marginBottom: "12px" }}>
                      <FaEye /> Live Generated QR Code Preview
                    </div>
                    
                    <div style={{ width: "170px", height: "170px", margin: "0 auto", padding: "8px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: "10px" }}>
                      <img
                        src={previewQrUrl}
                        alt="Live UPI QR Code Preview"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    </div>

                    <div style={{ marginTop: "12px", fontSize: "0.9rem", fontWeight: "800", color: "#0f172a" }}>
                      UPI ID: <span style={{ color: "#ea580c" }}>{effectiveUpiId}</span>
                    </div>
                    <p style={{ margin: "6px 0 0 0", fontSize: "0.78rem", color: "#64748b", lineHeight: "1.4" }}>
                      Scan with GPay or PhonePe to test. When saved, this QR will be generated live with <strong>auto-prefilled checkout amounts</strong> on both websites!
                    </p>
                  </div>
                )}

                <div style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "14px 16px", marginTop: "20px", display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <FaMagic style={{ color: "#16a34a", fontSize: "1.3rem", flexShrink: 0, marginTop: "2px" }} />
                  <div style={{ fontSize: "0.84rem", color: "#14532d", lineHeight: "1.5" }}>
                    <strong style={{ display: "block", marginBottom: "2px" }}>Automated Dynamic QR Generation Active</strong>
                    Scanning the generated QR code in GPay, PhonePe, Paytm, Navi, or BHIM automatically prefills the exact payable amount for subscriptions, ads, and sponsorships.
                  </div>
                </div>
              </div>

              {/* SECTION 3: RAZORPAY CREDENTIALS & GST */}
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", marginBottom: "25px" }}>
                <h3 style={{ fontSize: "1.1rem", fontWeight: "700", marginBottom: "12px", color: "var(--text-primary)" }}>
                  Razorpay API Keys & GST Tax Rate
                </h3>

                <div className="form-group">
                  <label>Razorpay Key ID</label>
                  <input
                    type="text"
                    name="razorpayKeyId"
                    value={keys.razorpayKeyId}
                    onChange={handleChange}
                    placeholder="rzp_live_xxx or rzp_test_xxx"
                    autoComplete="off"
                  />
                </div>

                <div className="form-group">
                  <label>Razorpay Key Secret</label>
                  <input
                    type="text"
                    name="razorpayKeySecret"
                    value={keys.razorpayKeySecret}
                    onChange={handleChange}
                    placeholder="Enter secret key"
                    style={{ WebkitTextSecurity: "disc", textSecurity: "disc" }}
                    autoComplete="off"
                  />
                </div>

                <div className="form-group">
                  <label>GST Tax Rate (%)</label>
                  <input
                    type="number"
                    name="gstRate"
                    value={keys.gstRate}
                    onChange={handleChange}
                    placeholder="e.g. 18"
                    min="0"
                    max="100"
                    required
                  />
                </div>
              </div>

              <button type="submit" className="save-btn" disabled={saving} style={{ width: "100%", padding: "14px", fontSize: "1rem", fontWeight: "700", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                <FaCheckCircle /> {saving ? "Saving Changes..." : "Save Payment Settings"}
              </button>

            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentSettings;
